import { isDevelopmentOnlyLoggingEnabled } from "@/utils/development-only-logging"
import { payloadKeyHash } from "@/shared/hash/payload-key-hash"

const PREFIX = "[CHECKOUT_ADDRESS_DIAG]"

export type CheckoutAddressDiagEvent =
  | "checkout_address_selected"
  | "checkout_address_restored"
  | "checkout_address_prompt_shown"
  | "checkout_address_deleted"

export type CheckoutAddressDiagPayload = {
  restaurantId?: string | null
  /** Hashed before logging — never emitted raw. */
  addressId?: string | null
  source?: "tap" | "auto" | "insert" | "edit"
  hasSelectedId?: boolean
  addressCount?: number
  /** Deletion only: whether the deleted address was the live checkout selection. */
  wasSelected?: boolean
}

function navigationType(): string | null {
  try {
    const entry = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined
    return entry?.type ?? null
  } catch {
    return null
  }
}

/**
 * Checkout address selection diagnostics. Same gate as the other `*_DIAG` loggers
 * (development only; production and test builds stay silent). Identifiers and state
 * transitions only — no address text, contact data, tokens, or customer objects.
 * Never throws and never influences state.
 */
export function logCheckoutAddressDiag(
  event: CheckoutAddressDiagEvent,
  payload: CheckoutAddressDiagPayload = {}
): void {
  if (!isDevelopmentOnlyLoggingEnabled()) return
  try {
    const { addressId, ...rest } = payload
    // eslint-disable-next-line no-console -- development-only diagnostics
    console.info(PREFIX, event, {
      ts: Date.now(),
      navigationType: navigationType(),
      ...rest,
      addressIdHash: addressId ? payloadKeyHash(String(addressId)) : null,
    })
  } catch {
    /* diagnostics must never affect checkout */
  }
}
